import { ApiClientError } from "@lumberset/api-client";
import { ago, date, dateTime, initials } from "@lumberset/takeoff-ui";
import {
  Avatar,
  Button,
  Container,
  Drawer,
  DropdownMenu,
  EmptyState,
  Field,
  Input,
  SectionHeader,
  SectionRow,
  TwoColumn,
  toast,
} from "@lumberset/ui";
import {
  ArrowUpRightOnBox,
  CheckCircle,
  EllipsisHorizontal,
  Envelope,
  Key,
  LockClosedSolid,
  LockOpenSolid,
  PencilSquare,
  ScrollText,
  UserGroup,
  Users,
} from "@medusajs/icons";
import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, type ErrorComponentProps, Link } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { auditSentence } from "../../../components/audit";
import { Crumbs } from "../../../components/crumbs";
import { SignInAsPrompt } from "../../../components/sign-in-as";
import {
  type PersonDetail,
  peopleActions,
  personQuery,
  useRefreshPerson,
} from "../../../lib/queries-people";
import { workspaceActions } from "../../../lib/queries-workspaces";
import { ConfirmEmailPrompt, EmailNotConfirmed } from "../staff/-email";
import { RoleBadge } from "../staff/-parts";
import { BlockPrompt } from "../yards/-members";
import { MenuText } from "../yards/-parts";
import { PersonStatus } from "./-parts";
import { WorkspacesSection } from "./-workspaces";

export const Route = createFileRoute("/_app/people/$id")({
  loader: ({ context: { queryClient }, params }) =>
    queryClient.ensureQueryData(personQuery(params.id)),
  component: PersonScreen,
  errorComponent: PersonError,
});

type Person = PersonDetail["person"];
type Opened = "edit" | "sign-in-app" | "sign-in-ops" | "block" | "email" | null;

/**
 * One person, customer or Staff: their details, their workspaces and role in
 * each, signing in as them, their sign-in and email, and what they did.
 */
function PersonScreen() {
  const { id } = Route.useParams();
  const { data } = useSuspenseQuery(personQuery(id));
  const { person, you, signInAs, block } = data;
  const { user: me } = Route.useRouteContext();
  const refresh = useRefreshPerson();
  const [opened, setOpened] = useState<Opened>(null);
  /** The workspace Sign in as opens, when it was asked for from one of their workspaces. */
  const [openIn, setOpenIn] = useState<string | undefined>(undefined);
  const staff = person.staffRole !== null;
  const blocked = person.status === "blocked";

  const restore = useMutation({
    mutationFn: () => workspaceActions.restoreSignIn(person.id),
    onSuccess: async (result) => {
      await refresh();
      toast.success("Sign-in restored", { description: result.message });
    },
    onError: (error) => toast.error(error.message),
  });

  const sendConfirmation = useMutation({
    mutationFn: () => workspaceActions.sendConfirmation(person.id),
    onSuccess: async (result) => {
      await refresh();
      toast.success("Confirmation email sent", { description: result.message });
    },
    onError: (error) => toast.error(error.message),
  });

  const passwordReset = useMutation({
    mutationFn: () => peopleActions.passwordReset(person.id),
    onSuccess: async (result) => {
      await refresh();
      if (!result.delivered) toast.error("The email did not go out. Try again in a minute.");
      else {
        toast.success(
          result.sent === "invitation" ? "Invitation sent again" : "Password link sent",
          { description: result.message },
        );
      }
    },
    onError: (error) => toast.error(error.message),
  });

  function signInAsHere(workspaceId?: string) {
    setOpenIn(workspaceId);
    setOpened("sign-in-app");
  }

  return (
    <>
      <Crumbs trail={[{ label: "People", to: "/people" }, { label: person.name }]} />

      <Container className="divide-y p-0">
        <SectionHeader
          level="h1"
          title={
            <span className="flex min-w-0 items-center gap-x-3">
              <Avatar fallback={initials(person.name)} shape="circle" />
              <span className="min-w-0 truncate">
                {person.name}
                {you ? (
                  <span className="txt-compact-small ml-2 font-normal text-ui-fg-muted">(you)</span>
                ) : null}
              </span>
            </span>
          }
          description={
            <span className="break-all">
              {person.title ? `${person.title}, ${person.email}` : person.email}
            </span>
          }
          actions={
            <>
              {signInAs.app === null ? (
                <Button size="small" variant="secondary" onClick={() => signInAsHere()}>
                  <ArrowUpRightOnBox /> Sign in as
                </Button>
              ) : null}
              <Button size="small" variant="secondary" onClick={() => setOpened("edit")}>
                <PencilSquare /> Edit
              </Button>
              <DropdownMenu>
                <DropdownMenu.Trigger asChild>
                  <Button size="small" variant="secondary" aria-label="More actions">
                    <EllipsisHorizontal />
                  </Button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Content align="end" className="max-w-[280px]">
                  {signInAs.app !== null && !you ? (
                    <DropdownMenu.Item disabled>
                      <ArrowUpRightOnBox />
                      <MenuText label="Sign in as" reason={signInAs.app} />
                    </DropdownMenu.Item>
                  ) : null}
                  {staff && !you ? (
                    <DropdownMenu.Item
                      disabled={signInAs.ops !== null}
                      onSelect={() => setOpened("sign-in-ops")}
                    >
                      <ArrowUpRightOnBox />
                      <MenuText
                        label="Sign in to the Staff panel as"
                        reason={
                          signInAs.ops ?? "Opens the Staff panel as they see it, for an hour."
                        }
                      />
                    </DropdownMenu.Item>
                  ) : null}
                  {person.onStaffPages ? (
                    <DropdownMenu.Item asChild>
                      <Link to="/staff/$id" params={{ id: person.id }}>
                        <Users /> Open their Staff page
                      </Link>
                    </DropdownMenu.Item>
                  ) : null}
                  {(signInAs.app !== null && !you) || staff || person.onStaffPages ? (
                    <DropdownMenu.Separator />
                  ) : null}
                  <DropdownMenu.Item disabled={blocked} onSelect={() => passwordReset.mutate()}>
                    <Key />
                    <MenuText
                      label="Send a password link"
                      reason={
                        blocked
                          ? "Their sign-in is turned off. Restore it first."
                          : `Opens the ${staff ? "Staff panel" : "Yard app"} to choose a new password.`
                      }
                    />
                  </DropdownMenu.Item>
                  {person.emailVerified ? null : (
                    <>
                      <DropdownMenu.Item
                        disabled={blocked}
                        onSelect={() => sendConfirmation.mutate()}
                      >
                        <Envelope /> Send a new confirmation email
                      </DropdownMenu.Item>
                      <DropdownMenu.Item onSelect={() => setOpened("email")}>
                        <CheckCircle /> Mark email as confirmed
                      </DropdownMenu.Item>
                    </>
                  )}
                  <DropdownMenu.Separator />
                  {block !== null ? (
                    <DropdownMenu.Item disabled>
                      <LockClosedSolid />
                      <MenuText
                        label={blocked ? "Restore sign-in" : "Block sign-in"}
                        reason={block}
                      />
                    </DropdownMenu.Item>
                  ) : blocked ? (
                    <DropdownMenu.Item onSelect={() => restore.mutate()}>
                      <LockOpenSolid /> Restore sign-in
                    </DropdownMenu.Item>
                  ) : (
                    <DropdownMenu.Item tone="danger" onSelect={() => setOpened("block")}>
                      <LockClosedSolid /> Block sign-in
                    </DropdownMenu.Item>
                  )}
                </DropdownMenu.Content>
              </DropdownMenu>
            </>
          }
        />

        <div className="txt-compact-small flex flex-wrap items-center gap-x-3 gap-y-2 px-6 py-3 text-ui-fg-subtle">
          {staff ? <RoleBadge role={person.staffRole} /> : null}
          <PersonStatus status={person.status} staff={staff} />
          {!person.emailVerified && person.status !== "unconfirmed" ? <EmailNotConfirmed /> : null}
          <span>
            {person.status === "invited"
              ? "Has not signed in yet"
              : person.lastActiveAt
                ? `Last active ${ago(person.lastActiveAt)}`
                : "No recent sign-in"}
          </span>
        </div>

        {blocked ? (
          <div className="flex flex-col gap-3 bg-ui-bg-subtle px-6 py-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="txt-small text-ui-fg-subtle">
              {staff ? "Deactivated" : "Sign-in blocked"}
              {person.banReason ? (
                <>
                  : <span className="text-ui-fg-base">{person.banReason}</span>
                </>
              ) : null}
              . They cannot sign in anywhere on LumberSet.
              {staff ? " Reactivate them on their Staff page." : ""}
            </p>
            {block === null ? (
              <Button
                size="small"
                variant="secondary"
                className="shrink-0"
                isLoading={restore.isPending}
                onClick={() => restore.mutate()}
              >
                <LockOpenSolid /> Restore sign-in
              </Button>
            ) : null}
          </div>
        ) : !person.emailVerified ? (
          <div className="flex flex-col gap-3 bg-ui-bg-subtle px-6 py-3 lg:flex-row lg:items-center lg:justify-between">
            <p className="txt-small text-ui-fg-subtle">
              Their email is not confirmed, so they cannot sign in until they open the link we email
              them. The link works for one hour.
            </p>
            <div className="flex shrink-0 flex-wrap gap-2">
              <Button
                size="small"
                variant="secondary"
                isLoading={sendConfirmation.isPending}
                onClick={() => sendConfirmation.mutate()}
              >
                <Envelope /> Send a new confirmation email
              </Button>
              <Button size="small" variant="secondary" onClick={() => setOpened("email")}>
                <CheckCircle /> Mark email as confirmed
              </Button>
            </div>
          </div>
        ) : null}
      </Container>

      <TwoColumn
        main={
          <WorkspacesSection
            data={data}
            onSignInAs={signInAs.app === null ? (workspaceId) => signInAsHere(workspaceId) : null}
          />
        }
        side={<Details person={person} />}
      />

      <Activity entries={data.activity} personId={person.id} />

      <Drawer open={opened === "edit"} onOpenChange={(open) => !open && setOpened(null)}>
        <Drawer.Content>
          {opened === "edit" ? (
            <EditForm
              person={person}
              emailLocked={data.changeEmail}
              onClose={() => setOpened(null)}
            />
          ) : null}
        </Drawer.Content>
      </Drawer>
      <SignInAsPrompt
        person={opened === "sign-in-app" ? { userId: person.id, name: person.name } : null}
        surface="app"
        workspaces={data.workspaces.map(({ id: workspaceId, name }) => ({ id: workspaceId, name }))}
        workspaceId={openIn}
        adminName={me.name}
        onClose={() => setOpened(null)}
      />
      <SignInAsPrompt
        person={opened === "sign-in-ops" ? { userId: person.id, name: person.name } : null}
        surface="ops"
        adminName={me.name}
        onClose={() => setOpened(null)}
      />
      <BlockPrompt
        person={opened === "block" ? { userId: person.id, name: person.name } : null}
        onDone={refresh}
        onClose={() => setOpened(null)}
      />
      <ConfirmEmailPrompt
        person={opened === "email" ? person : null}
        confirm={() => workspaceActions.confirmEmail(person.id)}
        onDone={refresh}
        onClose={() => setOpened(null)}
      />
    </>
  );
}

function Details({ person }: { person: Person }) {
  return (
    <Container className="divide-y p-0">
      <SectionHeader title="Details" />
      <SectionRow label="Email">
        <a
          href={`mailto:${person.email}`}
          className="break-all text-ui-fg-interactive hover:text-ui-fg-interactive-hover"
        >
          {person.email}
        </a>
      </SectionRow>
      <SectionRow label="Staff">
        {person.staffRole ? (
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <RoleBadge role={person.staffRole} />
            <Link
              to="/staff/$id"
              params={{ id: person.id }}
              className="txt-compact-small-plus text-ui-fg-interactive hover:text-ui-fg-interactive-hover"
            >
              Their Staff page
            </Link>
          </span>
        ) : person.onStaffPages ? (
          <Link
            to="/staff/$id"
            params={{ id: person.id }}
            className="txt-compact-small-plus text-ui-fg-interactive hover:text-ui-fg-interactive-hover"
          >
            Former Staff
          </Link>
        ) : (
          "Not Staff"
        )}
      </SectionRow>
      <SectionRow label="Joined">{date(person.createdAt)}</SectionRow>
      <SectionRow label="Last active">
        {person.lastActiveAt ? dateTime(person.lastActiveAt) : null}
      </SectionRow>
    </Container>
  );
}

/**
 * Their name and the email they sign in with. Sends only what changed.
 * Another Admin's email is theirs to change, so it shows but cannot be edited.
 */
function EditForm({
  person,
  emailLocked,
  onClose,
}: {
  person: Person;
  /** Why their email cannot be changed here, or null when it can. */
  emailLocked: string | null;
  onClose: () => void;
}) {
  const refresh = useRefreshPerson();
  const save = useMutation({
    mutationFn: (form: FormData) => {
      const text = (name: string) => String(form.get(name) ?? "").trim();
      const json: { name?: string; email?: string } = {};
      if (text("name") && text("name") !== person.name) json.name = text("name");
      if (
        emailLocked === null &&
        text("email") &&
        text("email").toLowerCase() !== person.email.toLowerCase()
      ) {
        json.email = text("email");
      }
      return peopleActions.edit(person.id, json);
    },
    onSuccess: async (result) => {
      await refresh();
      if (result.changed.length > 0) toast.success(`${result.person.name} saved`);
      onClose();
    },
    onError: (error) => toast.error(error.message),
  });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    save.mutate(new FormData(event.currentTarget));
  }

  return (
    <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
      <Drawer.Header>
        <Drawer.Title>Edit {person.name}</Drawer.Title>
        <Drawer.Description>
          {emailLocked === null
            ? "Both changes are kept in the audit log with your name."
            : "The change is kept in the audit log with your name."}
        </Drawer.Description>
      </Drawer.Header>

      <Drawer.Body className="flex flex-col gap-y-5">
        <Field label="Name">
          {(control) => (
            <Input {...control} name="name" required maxLength={120} defaultValue={person.name} />
          )}
        </Field>
        <Field
          label="Email"
          hint={
            emailLocked ??
            (person.emailVerified
              ? "They sign in with this address from now on. Their email stays confirmed."
              : "They sign in with this address from now on. It stays unconfirmed until they open a confirmation link.")
          }
        >
          {(control) => (
            <Input
              {...control}
              name="email"
              type="email"
              required
              disabled={emailLocked !== null}
              maxLength={254}
              autoComplete="off"
              defaultValue={person.email}
            />
          )}
        </Field>
      </Drawer.Body>

      <Drawer.Footer>
        <Button type="button" variant="secondary" size="small" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" size="small" isLoading={save.isPending}>
          Save
        </Button>
      </Drawer.Footer>
    </form>
  );
}

/** What they did, and what was done to their account, newest first. */
function Activity({ entries, personId }: { entries: PersonDetail["activity"]; personId: string }) {
  return (
    <Container className="divide-y p-0">
      <SectionHeader
        title="Activity"
        description="What they did, and what Admins changed about them, from the audit log. The latest 50 entries."
      />
      {entries.length === 0 ? (
        <EmptyState
          className="py-10"
          icon={<ScrollText />}
          title="Nothing recorded yet"
          description="Changes they make, and changes to their sign-in, email and workspaces, land here."
        />
      ) : (
        <ul className="divide-y divide-ui-border-base">
          {entries.map((entry) => {
            const hasDetails = entry.data !== null && Object.keys(entry.data).length > 0;
            const who =
              entry.actorId === personId
                ? entry.impersonatorId
                  ? `As them, by Admin ${entry.impersonatorName ?? "(removed)"}`
                  : "Them"
                : (entry.actorName ?? "System");
            return (
              <li key={entry.id} className="px-6 py-3">
                <p className="txt-compact-small break-words text-ui-fg-base" title={entry.action}>
                  {auditSentence(entry.action, entry.data)}
                </p>
                <p className="txt-compact-xsmall text-ui-fg-muted">
                  {who}
                  {entry.workspaceName ? `, in ${entry.workspaceName}` : ""},{" "}
                  {dateTime(entry.createdAt)}
                </p>
                {hasDetails ? (
                  <details className="group mt-1">
                    <summary className="txt-compact-xsmall w-fit cursor-pointer select-none rounded text-ui-fg-muted outline-none hover:text-ui-fg-subtle focus-visible:shadow-borders-focus">
                      Details
                    </summary>
                    <pre className="txt-compact-xsmall mt-1.5 max-h-60 overflow-auto whitespace-pre-wrap break-all rounded-md bg-ui-bg-subtle px-3 py-2 font-mono text-ui-fg-subtle">
                      {JSON.stringify(entry.data, null, 2)}
                    </pre>
                  </details>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </Container>
  );
}

function PersonError({ error, reset }: ErrorComponentProps) {
  const missing = error instanceof ApiClientError && error.status === 404;
  return (
    <Container>
      <EmptyState
        icon={<UserGroup />}
        title={missing ? "No such person" : "This person could not be loaded"}
        description={
          missing
            ? "Their account may have been deleted. Their entries stay in the audit log."
            : error instanceof Error
              ? error.message
              : "Please try again."
        }
        action={
          missing ? (
            <Button variant="secondary" asChild>
              <Link to="/people">Back to People</Link>
            </Button>
          ) : (
            <Button variant="secondary" onClick={reset}>
              Try again
            </Button>
          )
        }
      />
    </Container>
  );
}
